/**
 * useVersionCheck — Cache Buster
 *
 * Her CHECK_INTERVAL_MS'de bir `/version.json?ts=Date.now()` fetch eder
 * (query string cache bypass). İlk yüklemede build_id'yi memory'ye kaydeder;
 * sonraki her fetch'te farklıysa kullanıcıya "yeni sürüm var" toast'ı gösterir
 * ve tıklandığında hard reload yapar (`window.location.reload(true)`).
 *
 * Notlar:
 * - index.html no-cache meta ile zaten cache'lenmiyor ama service worker /
 *   Cloudflare edge cache olabilir; bu hook o durumu da yakalar
 * - 520 aldıktan sonra kullanıcı sayfayı yenilese bile eski HTML gelirse
 *   bu hook 60 sn içinde farkı yakalayıp yeni sürüme geçirir
 * - Kullanıcı bir modal açıksa/veri yazıyorsa OTOMATIK reload YAPMAZ,
 *   sadece toast gösterir; kullanıcı elle "Şimdi Yenile" der.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

const VERSION_URL = "/version.json";
const CHECK_INTERVAL_MS = 60_000; // 60 sn
const LS_KEY = "akb_last_build_id";

export function useVersionCheck() {
  const currentBuildId = useRef(null);

  useEffect(() => {
    let stopped = false;

    const check = async () => {
      try {
        const res = await fetch(`${VERSION_URL}?ts=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!res.ok) return;
        const data = await res.json();
        const newId = data.build_id;
        if (!newId) return;

        if (currentBuildId.current === null) {
          currentBuildId.current = newId;
          localStorage.setItem(LS_KEY, newId);
          return;
        }
        if (newId !== currentBuildId.current) {
          if (stopped) return;
          currentBuildId.current = newId;
          localStorage.setItem(LS_KEY, newId);
          toast.info("Yeni sürüm hazır — güncellemek için tıklayın", {
            id: "version-update-toast",
            duration: 30000,
            action: {
              label: "Şimdi Yenile",
              onClick: () => {
                // Hard reload: cache bypass
                window.location.reload();
              },
            },
          });
        }
      } catch {
        /* offline / net hatası: sessiz geç */
      }
    };

    // İlk check'i hemen yap
    check();
    const iv = setInterval(check, CHECK_INTERVAL_MS);

    // Sekme tekrar aktif olduğunda da check et (uzun süre background'da kaldıysa)
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
}

export default useVersionCheck;
